/**
 * MSAL.js v3 (PKCE) configuration for delegated Microsoft Graph access.
 *
 * Penser's tenant id and client id should be populated at deploy time via
 *   window.__MSAL_CONFIG__ = { clientId, tenantId }
 * which Amplify Hosting injects from environment variables. Local mock-mode
 * skips MSAL entirely (see services/api.ts MOCK_MODE check).
 */

import { PublicClientApplication, type Configuration, InteractionRequiredAuthError } from '@azure/msal-browser'

export const GRAPH_SCOPES = ['Mail.Read', 'offline_access', 'User.Read']

/**
 * SharePoint / OneDrive read scopes for the assistant's document search
 * (#6F2uoh59). Deliberately NOT folded into GRAPH_SCOPES: these two are
 * admin-consent-grade in most tenants, and adding them to the mail scope set
 * would make the ENTIRE Outlook connect fail with consent_required wherever the
 * grant is missing — regressing a feature that works today for one that might
 * not be approved yet. Requested incrementally, and only ever silently (see
 * acquireSharePointToken), so a tenant without the grant simply has no
 * SharePoint search instead of a broken mailbox.
 */
export const SHAREPOINT_SCOPES = ['Sites.Read.All', 'Files.Read.All']

type RuntimeConfig = { clientId: string; tenantId?: string; m365ApiScope?: string }

// Runtime config is loaded once at app start. Three sources, in order:
//   1. window.__MSAL_CONFIG__ — set by an inline <script> in index.html
//      (highest priority; useful for local dev override)
//   2. /msal-config.json — static file in public/ that Amplify Hosting
//      serves alongside the bundle. Operators edit this without a rebuild.
//   3. null — Outlook stays disabled, ConnectButton shows the
//      "unavailable in mock mode" message.
let _cachedConfig: RuntimeConfig | null | undefined = undefined

async function loadConfig(): Promise<RuntimeConfig | null> {
  if (_cachedConfig !== undefined) return _cachedConfig
  const w = window as unknown as { __MSAL_CONFIG__?: RuntimeConfig }
  if (w.__MSAL_CONFIG__?.clientId) {
    _cachedConfig = w.__MSAL_CONFIG__
    return _cachedConfig
  }
  try {
    const r = await fetch('/msal-config.json', { cache: 'no-store' })
    if (r.ok) {
      const cfg = await r.json() as RuntimeConfig & { _comment?: string }
      if (cfg.clientId) {
        _cachedConfig = {
          clientId: cfg.clientId,
          tenantId: cfg.tenantId,
          m365ApiScope: cfg.m365ApiScope,
        }
        return _cachedConfig
      }
    }
  } catch { /* ignore */ }
  _cachedConfig = null
  return null
}

// Synchronous variant used by isMsalAvailable() etc. — relies on loadConfig()
// having already populated the cache (which happens during bootstrap).
function getConfig(): RuntimeConfig | null {
  if (_cachedConfig !== undefined) return _cachedConfig
  const w = window as unknown as { __MSAL_CONFIG__?: RuntimeConfig }
  if (w.__MSAL_CONFIG__?.clientId) return w.__MSAL_CONFIG__
  return null
}

// Called once at app start from main.tsx so the rest of the app can use
// the sync getConfig() / isMsalAvailable() without awaiting a fetch.
export async function preloadMsalConfig(): Promise<void> {
  await loadConfig()
}

let _instance: PublicClientApplication | null = null

export function getMsal(): PublicClientApplication | null {
  if (_instance) return _instance
  const cfg = getConfig()
  if (!cfg) return null
  const msalConfig: Configuration = {
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId ?? 'common'}`,
      redirectUri: `${window.location.origin}/outlook/return`,
    },
    cache: {
      // localStorage, not sessionStorage (#outlook-auto-login). ssoSilent is the
      // fragile half of auto-connect: it needs Entra's session cookie inside a
      // hidden third-party iframe, which Chrome tracking protection and Safari
      // ITP block outright — for those users it fails on EVERY page load and
      // they see the Connect button every single session. With the token cache
      // in localStorage, one manual connect (or one successful ssoSilent)
      // persists across tabs and browser restarts, so auto-connect resolves via
      // the cached account ('already') without touching the iframe again.
      // Tokens are scoped to Mail.Read/User.Read and clear on Disconnect.
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  }
  _instance = new PublicClientApplication(msalConfig)
  void _instance.initialize()
  return _instance
}

export function isMsalAvailable(): boolean {
  return getConfig() !== null
}

let _ready: Promise<void> | null = null

/**
 * Resolves once MSAL has initialized AND drained any pending redirect response
 * — i.e. once `getConnectedAccount()` can be trusted.
 *
 * Reading the account before this settles is a bug that has now been written
 * three times in this codebase: MSAL's account cache is empty until
 * handleRedirectPromise() has run, so a synchronous read right after sign-in
 * reports "not connected" and nothing re-triggers it. getMsal() fires
 * initialize() with void-fire, which is deliberately NOT awaited, so having
 * an instance is not the same as being ready.
 *
 * Cached: concurrent callers share one initialization and later calls resolve
 * immediately. The cache is per page-load, which is the right lifetime — the
 * Microsoft redirect is a full navigation, so a returning user gets a fresh
 * module and a fresh drain.
 */
export function ensureMsalReady(): Promise<void> {
  if (_ready) return _ready
  const msal = getMsal()
  if (!msal) return (_ready = Promise.resolve())
  _ready = (async () => {
    try {
      await msal.initialize()
      await msal.handleRedirectPromise()
    } catch {
      // Settle regardless. Callers only need the account cache to be readable;
      // a genuine auth failure surfaces on the /outlook/return page instead.
    }
  })()
  return _ready
}

// MSAL.js stores an `interaction.status` flag in sessionStorage while a
// redirect/popup flow is in progress. If our /outlook/return handler runs
// but the URL had no auth response (silent failure mode), the flag stays
// stuck and every subsequent loginRedirect throws BrowserAuthError:
// interaction_in_progress with no visible UI feedback — the button
// "stops working" from the user's perspective. Clear it proactively.
function clearStuckInteractionFlag(): void {
  // Scan both storages: MSAL keeps temporary/interaction state in
  // sessionStorage, but with cacheLocation localStorage some builds mirror
  // flags there too. Clearing a key that doesn't exist is a no-op.
  for (const storage of [sessionStorage, localStorage]) {
    try {
      const keys: string[] = []
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i)
        if (k && k.startsWith('msal.') && k.endsWith('.interaction.status')) keys.push(k)
      }
      for (const k of keys) storage.removeItem(k)
    } catch { /* private mode etc — ignore */ }
  }
}

// "The user pressed Disconnect Outlook." Auto-connect (below) fires on every
// mount, so without a sticky opt-out the button would be a no-op: disconnect,
// re-render, silently reconnect. localStorage on purpose — it matches MSAL's
// cacheLocation (above), so the opt-out and the token cache have exactly the
// same lifetime: both survive tab close, both fall to an explicit reconnect.
const DISCONNECTED_KEY = 'penser:outlook-disconnected'

function isOutlookDisconnected(): boolean {
  try { return localStorage.getItem(DISCONNECTED_KEY) === '1' } catch { return false }
}

function setOutlookDisconnected(v: boolean): void {
  try {
    if (v) localStorage.setItem(DISCONNECTED_KEY, '1')
    else localStorage.removeItem(DISCONNECTED_KEY)
  } catch { /* private mode — the flag is a convenience, not a security control */ }
}

/**
 * Outcome of the automatic (no-click) Outlook connect.
 *
 * `needs_click` is the honest failure state and it is NOT rare: silent SSO
 * depends on the tenant having consented to the Graph scopes, on no
 * conditional-access interrupt, and on the browser allowing MSAL's hidden
 * iframe to login.microsoftonline.com. Callers must keep offering the manual
 * button for it.
 */
export type AutoConnectOutcome =
  | 'unavailable'   // MSAL isn't configured (mock mode) — nothing to connect
  | 'already'       // a Microsoft account was already in the MSAL cache
  | 'connected'     // silent SSO succeeded; the user never saw a prompt
  | 'declined'      // they pressed Disconnect this session; don't reconnect
  | 'needs_click'   // Entra requires interaction — fall back to the button

let _autoConnect: Promise<AutoConnectOutcome> | null = null

/**
 * Authenticate Microsoft Graph off the portal's own SSO session (#eIL794Eg,
 * #m0upPwcI) — no second sign-in step.
 *
 * How it can work at all: portal login federates Cognito to Microsoft Entra, so
 * by the time the app renders the browser already holds an Entra session cookie
 * for `loginHint`. `ssoSilent` rides that cookie (`prompt=none` in a hidden
 * iframe) and comes back with a Graph token without any user interaction.
 *
 * Honest caveats — every one of these lands the user back on the button:
 *  - the Graph scopes must already be consented for the tenant (a first-ever
 *    consent CANNOT be silent, by design);
 *  - conditional access / MFA-per-resource interrupts force interaction;
 *  - the iframe needs `frame-src https://login.microsoftonline.com` in the CSP
 *    (added to customHttp.yml with this change — `default-src 'self'` was
 *    silently blocking it) and third-party-cookie restrictions can still kill
 *    it in some browsers.
 *
 * Memoised per page load: two components read the account hook, and a second
 * ssoSilent while the first is in flight throws interaction_in_progress.
 */
export function autoConnectOutlook(loginHint: string): Promise<AutoConnectOutcome> {
  // Don't memoise a no-hint attempt: the caller is probably still loading the
  // auth context and will come back with the real email a tick later.
  if (!loginHint) return Promise.resolve<AutoConnectOutcome>('needs_click')
  if (_autoConnect) return _autoConnect
  _autoConnect = (async (): Promise<AutoConnectOutcome> => {
    const msal = getMsal()
    if (!msal) return 'unavailable'
    if (isOutlookDisconnected()) return 'declined'
    await ensureMsalReady()
    if (msal.getAllAccounts().length > 0) return 'already'
    try {
      const result = await msal.ssoSilent({ scopes: GRAPH_SCOPES, loginHint })
      if (result?.account) {
        msal.setActiveAccount(result.account)
        return 'connected'
      }
      return 'needs_click'
    } catch (err) {
      // Expected whenever consent/CA/iframe policy blocks it. console.info, not
      // error: this is a designed fallback, and a red console entry on every
      // page load would train people to ignore real MSAL errors.
      const e = err as { errorCode?: string }
      console.info('[msal] silent SSO to Graph unavailable, manual connect needed:', e.errorCode ?? err)
      return 'needs_click'
    }
  })()
  return _autoConnect
}

export async function loginRedirect(): Promise<void> {
  const msal = getMsal()
  if (!msal) {
    alert('MSAL is not configured for this environment. Set window.__MSAL_CONFIG__ before connecting Outlook.')
    return
  }
  // initialize() in MSAL.js v3 is required before any auth method and is
  // idempotent. getMsal() fires it once with void-fire — awaiting again here
  // guarantees the redirect call has a fully-initialized client.
  await msal.initialize()
  // Drain any handle-redirect-promise that might still be queued from a
  // half-completed prior flow. Returning a result here is fine — we ignore
  // it; the goal is to flush MSAL's internal state.
  try { await msal.handleRedirectPromise() } catch { /* ignored */ }
  clearStuckInteractionFlag()
  // An explicit click is consent to reconnect — clear the Disconnect opt-out so
  // auto-connect works again next load.
  setOutlookDisconnected(false)
  try {
    await msal.loginRedirect({ scopes: GRAPH_SCOPES })
  } catch (err) {
    console.error('[msal] loginRedirect threw', err)
    const e = err as { errorCode?: string; errorMessage?: string; message?: string }
    // If the in-progress flag has reasserted itself between our clear and
    // the redirect call, retry once after another clear pass.
    if (e.errorCode === 'interaction_in_progress') {
      clearStuckInteractionFlag()
      try { await msal.loginRedirect({ scopes: GRAPH_SCOPES }) } catch { /* fall through */ }
      return
    }
    alert(`Outlook sign-in failed: ${e.errorCode ?? ''} ${e.errorMessage ?? e.message ?? ''}`.trim())
  }
}

export async function acquireToken(): Promise<string | null> {
  const msal = getMsal()
  if (!msal) return null
  // Wait for initialize() + the redirect drain before reading accounts.
  // getAllAccounts() is empty until MSAL has hydrated, so an early call returned
  // null and the caller silently lost live Graph access — for the assistant that
  // means /ai/chat gets no outlook_token and the backend quietly falls back to
  // cached email_attachments, so it answers from stale mail without saying so
  // (#kga2UVRQ, the email half). The UI gate was fixed in a5c25de; this read
  // was not, and it's the one the chat adapter actually depends on.
  await ensureMsalReady()
  const accounts = msal.getAllAccounts()
  if (accounts.length === 0) return null
  try {
    const result = await msal.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] })
    return result.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes: GRAPH_SCOPES })
    }
    return null
  }
}

export function getConnectedAccount(): { email: string; name: string } | null {
  const msal = getMsal()
  if (!msal) return null
  const accounts = msal.getAllAccounts()
  if (accounts.length === 0) return null
  const acct = accounts[0]
  return {
    email: acct.username,
    name: acct.name ?? acct.username,
  }
}

// Negative cache for the SharePoint scopes. Without consent, every
// acquireTokenSilent costs a refresh-token POST plus a silent-iframe round-trip
// before it fails — and this runs on EVERY chat turn, so an unconsented tenant
// would pay ~1-2s of dead latency per message forever. One failure is enough to
// know; consent can't be granted mid-session without a redirect that reloads the
// page and resets this anyway.
let _sharePointUnavailable = false
let _m365Unavailable = false

/**
 * Graph token for SharePoint / OneDrive document search (#6F2uoh59).
 *
 * SILENT ONLY, on purpose. If the tenant hasn't consented to Sites.Read.All this
 * returns null and the assistant reports "SharePoint isn't authorised" — it must
 * never escalate to a redirect, because that would yank the user out of an
 * in-flight chat to a Microsoft consent screen they can't approve themselves.
 */
export async function acquireSharePointToken(): Promise<string | null> {
  if (_sharePointUnavailable) return null
  const msal = getMsal()
  if (!msal) return null
  await ensureMsalReady()
  const accounts = msal.getAllAccounts()
  if (accounts.length === 0) return null
  try {
    const result = await msal.acquireTokenSilent({ scopes: SHAREPOINT_SCOPES, account: accounts[0] })
    return result.accessToken
  } catch {
    _sharePointUnavailable = true
    return null
  }
}

/** Audience-restricted token for Foundry's shared Microsoft MCP endpoint.
 *
 * This is not a Graph token. Foundry validates the access_as_user scope and
 * exchanges it for Graph access with OBO, so the BD backend never receives a
 * reusable Graph token for the shared-service path.
 */
export async function acquireM365Token(): Promise<string | null> {
  if (_m365Unavailable) return null
  const cfg = getConfig()
  const scope = cfg?.m365ApiScope?.trim()
  if (!scope) return null
  const msal = getMsal()
  if (!msal) return null
  await ensureMsalReady()
  const accounts = msal.getAllAccounts()
  if (accounts.length === 0) return null
  try {
    const result = await msal.acquireTokenSilent({ scopes: [scope], account: accounts[0] })
    return result.accessToken
  } catch {
    _m365Unavailable = true
    return null
  }
}

/**
 * Disconnect Outlook: drop the Microsoft tokens this app holds, and remember the
 * choice so auto-connect doesn't immediately undo it.
 *
 * This used to be `logoutPopup`, which is wrong now that Graph auth rides the
 * portal's own SSO session: logoutPopup hits Entra's end-session endpoint and
 * signs the user out of Microsoft *browser-wide* — the same session Cognito
 * federates to for portal login. "Stop the AI reading my mail" would have
 * knocked out their sign-in to everything. clearCache revokes this app's access
 * locally, which is the actual ask.
 */
export async function signOutOutlook(): Promise<void> {
  const msal = getMsal()
  // Set the opt-out even if MSAL is unavailable, so the state is consistent.
  setOutlookDisconnected(true)
  _autoConnect = null
  if (!msal) return
  const accounts = msal.getAllAccounts()
  await msal.clearCache(accounts.length > 0 ? { account: accounts[0] } : undefined)
  msal.setActiveAccount(null)
}
